/**
 * The headline figures, in one place.
 *
 * They used to be inline in two components and disagreed about which ones to
 * show: the home page gave companies / contacts / websites / lists / focus,
 * while `landing-trust-bar` — which all seven keyword pages rendered — gave
 * companies / contacts / accuracy / freshness and omitted the websites count
 * entirely. That component is gone: `landing-hero.svelte` renders
 * `TRUST_STATS` inside the hero itself now, so there is one less place for a
 * page to forget a bar.
 *
 * The three figures below are the canonical ones, and every stat bar derives
 * them from these constants rather than restating them. The claims either bar
 * adds on top (accuracy, update frequency, predefined lists, Swiss focus) are
 * kept as they were.
 *
 * These are visible text on pages that rank, so a figure edited here rewrites
 * what Google and the language models read off eight pages. That is the point
 * of the indirection: it is one edit, and it cannot leave the two bars
 * disagreeing again.
 *
 * # The constants are not the whole job
 *
 * Contacts and websites were raised on 2026-10-19 and the edit touched
 * thirteen files, not one. Companies went 700k+ to 750k+ on 2026-10-20 and
 * took 49 replacements across twenty, including five blog posts, nine page
 * titles and `llms.txt`. The indirection covers the stat bars; it does not
 * cover hero badges, meta descriptions, JSON-LD, `llms.txt` or the three blog
 * posts that quote a figure in running prose, all of which spell the number
 * out. Before changing one of these, grep the old value across `src/` and
 * `static/` — the constant is the floor, not the ceiling.
 *
 * `kmu-adressen-schweiz` claims 600’000+ SMEs and is deliberately left alone:
 * it is a subset of the 750k rather than a copy of it, and may well be right.
 */
export type Stat = { value: string; label: string };

/** Swiss companies in the pool. */
export const COMPANIES = '750k+';
/** Decision-maker contacts attached to them. */
export const CONTACTS = '2.6M+';
/** Company websites crawled and indexed. */
export const WEBSITES = '3.1M';

/** The home page's five. */
export const PLATFORM_STATS: Stat[] = [
  { value: COMPANIES, label: 'Unternehmen' },
  { value: CONTACTS, label: 'Kontakte' },
  { value: WEBSITES, label: 'Websites indexiert' },
  { value: '230+', label: 'vordefinierte Listen' },
  { value: 'CH', label: 'Fokus' }
];

/**
 * The Data Cleaning page's five.
 *
 * Service metrics rather than pool size, so none of the three canonical
 * figures appear here and there is nothing to keep in step. The Intelligence
 * page shows `PLATFORM_STATS` — the same five as the home page, which is why
 * it has no set of its own.
 */
export const CLEANING_STATS: Stat[] = [
  { value: '20%', label: 'Daten jährlich ungültig' },
  { value: '5', label: 'Reinigungsstufen' },
  { value: '95%+', label: 'Genauigkeit' },
  { value: '24-48h', label: 'Bearbeitungszeit' },
  { value: 'CH', label: 'Datenverarbeitung' }
];

/**
 * How many filters the search offers.
 *
 * # One option is one filter
 *
 * An earlier count read "one criterion is one condition a customer can set"
 * and arrived at 32. That counts controls, not filters, and it undersells the
 * thing by an order of magnitude: picking Zürich and picking Thurgau are two
 * different filters, not two settings of one. The rule is the plain one —
 * **every option a customer can tick is a filter.**
 *
 * Counted 2026-10-19:
 *
 *     Technologien (von rund 8’000 erkannten angeboten)   ~300
 *     Zertifikate und Normen                                56
 *     Kantone                                               26
 *     Kontaktfunktionen                                     11
 *     Rechtsformen                                          18
 *     Grossregionen                                          7
 *     Kontaktkanäle                                          7
 *     E-Mail-Setup (mail gaps)                               7
 *     Signalereignisse                                       7
 *     Sprachgebiete                                          4
 *     Vertriebsmodelle                                       4
 *     Transportlizenzarten                                   3
 *     Konzernrollen                                          2
 *     Bereiche, Schalter und Freitext — Mitarbeiterzahl,
 *     Gründungsjahr, Kapital, Filialzahl, Fuhrparkgrösse,
 *     Radius, PLZ-Liste, Beschreibung, Textbedingungen,
 *     Titelsuche, Lehrbetrieb, offene Stellen,
 *     Revisionsverzicht, Revisionsstelle, Kundenliste       ~15
 *                                                          ----
 *                                                          ~467
 *
 * # Why "450+" and not 478
 *
 * The largest term is itself an approximation: roughly 300 of the ~8,000
 * detected technologies are offered, and `technologies.go` describes that
 * vocabulary changing five times in the week it was written. A total carried
 * to three figures would be false precision resting on a round number.
 *
 * Contact functions are 11, and the arithmetic has been 22 twice by mistake.
 * `de.json` holds 22 role labels, but only 11 are offered in the picker —
 * see `ROLES` in `content/firmendaten.ts`, which is the authority and has
 * eleven members. Counting the labels in the app's locale file is how this
 * line gets wrong; count `ROLES`.
 *
 * "450+" is below every plausible reading of the arithmetic, which is the
 * right direction for a claim to be wrong in.
 */
export const FILTER_COUNT: string | null = '450+';

/**
 * The keyword landing pages' five.
 *
 * Two changed on 2026-10-20, both because nothing backed them. "Verifizierte
 * Kontakte" became "Kontakte" — the contacts are publicly named, which is a
 * different claim from verified — and "95%+ Genauigkeit" became the filter
 * count, which is a figure this site can show the working for. The accuracy
 * number appeared on nine pages and was measured nowhere.
 */
export const TRUST_STATS: Stat[] = [
  { value: COMPANIES, label: 'Schweizer Firmen' },
  { value: CONTACTS, label: 'Kontakte' },
  { value: WEBSITES, label: 'Websites indexiert' },
  { value: FILTER_COUNT ?? '450+', label: 'Filter' },
  { value: 'Täglich', label: 'Aktualisiert' }
];
